"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { KIT_STATUSES } from "@/lib/constants/kit-statuses";
import { SHOW_TYPES } from "@/lib/constants/show-types";

// Road-show event = an `on_road` show. Fetched from the venue's shows.
// `min`/`max` come from the show's participant range; min is "soft" (advisory,
// §8.1) and max — where set — is a hard cap.
interface RoadShowEventOption {
  value: string; // the show id (as string, for the Select)
  showId: number;
  label: string;
  min: number;
  max: number | null;
  basePrice: number;
}

// Kits are the venue's standalone kit records (Kit CRUD). Optional /
// informational here (doc §10.1) — availability is driven by the kit block.
interface KitOption {
  value: string; // the kit id (as string)
  label: string;
}

interface NewRoadShowModalProps {
  isOpen: boolean;
  onClose: () => void;
  venueId: string;
  portalType: string;
  venueCurrency?: string;
  venueName?: string;
}

export function NewRoadShowModal({
  isOpen,
  onClose,
  venueId,
  portalType,
  venueCurrency = "SEK",
  venueName,
}: NewRoadShowModalProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [eventType, setEventType] = useState<string>("");
  const [kit, setKit] = useState<string>("");
  const [participants, setParticipants] = useState("30");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");

  // Event types (on_road shows) and kits, fetched from the DB when the modal opens.
  const [events, setEvents] = useState<RoadShowEventOption[]>([]);
  const [kits, setKits] = useState<KitOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const [showsRes, kitsRes] = await Promise.all([
          fetch(`/api/venues/${venueId}/shows`),
          fetch(`/api/venues/${venueId}/kits`),
        ]);

        if (!cancelled && showsRes.ok) {
          const data = await showsRes.json();
          const roadEvents: RoadShowEventOption[] = (data.shows || [])
            .filter(
              (s: any) =>
                s.show_type === SHOW_TYPES.ON_ROAD &&
                s.status_id === STATUS_IDS.SHOW_ACTIVE,
            )
            .map((s: any) => {
              const name =
                s.show_infos?.find((i: any) => i.lang === "en")?.name ||
                s.show_infos?.[0]?.name ||
                "Unnamed road show";
              return {
                value: String(s.id),
                showId: s.id,
                label: name,
                min: s.min_participants ?? 1,
                max: s.max_participants ?? null,
                basePrice: Number(s.base_price ?? 0),
              };
            });
          setEvents(roadEvents);
        }

        if (!cancelled && kitsRes.ok) {
          const data = await kitsRes.json();
          const kitOptions: KitOption[] = (data.kits || [])
            .filter((k: any) => k.status === KIT_STATUSES.ACTIVE)
            .map((k: any) => ({
              value: String(k.id),
              label: k.name,
            }));
          setKits(kitOptions);
        }
      } catch (error) {
        console.error("Error loading road show options:", error);
      } finally {
        if (!cancelled) setIsLoadingOptions(false);
      }
    };

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [isOpen, venueId]);

  const selectedEvent = events.find((e) => e.value === eventType);
  const participantCountNum = parseInt(participants, 10) || 0;
  const belowMin =
    !!selectedEvent && participantCountNum > 0 && participantCountNum < selectedEvent.min;

  const handleCreate = async () => {
    // Validate required fields
    if (!customerName.trim()) {
      toast.error("Please enter customer name");
      return;
    }
    if (!customerEmail.trim()) {
      toast.error("Please enter customer email");
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!eventType) {
      toast.error("Please select an event type");
      return;
    }
    const participantCount = parseInt(participants, 10);
    if (!participantCount || participantCount < 1) {
      toast.error("Please enter the number of participants");
      return;
    }
    // Hard max where the event sets one (e.g. Dinner ≤ 35). Min is "soft" (§8.1)
    // — surfaced as a hint, not blocked.
    if (selectedEvent?.max != null && participantCount > selectedEvent.max) {
      toast.error(
        `${selectedEvent.label} allows at most ${selectedEvent.max} participants`,
      );
      return;
    }
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }
    if (!startTime || !endTime) {
      toast.error("Please select start and end times");
      return;
    }

    // Validate time range
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    if (start >= end) {
      toast.error("End time must be after start time");
      return;
    }

    // Show duration in minutes (from the entered show time), so the booking's
    // end time reflects the actual show window.
    const customDuration = Math.round(
      (end.getTime() - start.getTime()) / 60000,
    );

    // Price is set later on the booking detail page.
    const priceValue = 0;

    setIsLoading(true);

    try {
      // Create timestamp for the show session
      const showSessionTimeslot = `${format(selectedDate, "yyyy-MM-dd")}T${startTime}:00.000Z`;

      const bookingData = {
        // Customer info
        name: customerName.trim(),
        email: customerEmail.trim(),
        phone: "0000000000", // Placeholder phone

        // Booking details
        participants: participantCount,
        bookingType: "roadshow", // This will be converted to booking_type_id by the API
        isDraft: true,
        statusId: STATUS_IDS.BOOKING_DRAFT,

        // Pricing - using fixed pricing for road shows
        totalPrice: priceValue,
        currency: venueCurrency,
        priceMode: "fixed", // Road shows always use fixed pricing
        paymentMethod: "invoice", // Default to invoice for road shows

        // The selected on_road show. Pricing IDs stay as placeholders for now —
        // road-show pricing is fixed and set on the booking detail page.
        shows: [{
          showId: selectedEvent?.showId ?? Number(eventType),
          showSessionTimeslot: showSessionTimeslot,
          assignedPricingId: 1,
          showTimeSlotPricingId: 1,
          pricePointId: 1,
          price: priceValue,
          date: format(selectedDate, "yyyy-MM-dd"),
          time: startTime,
          dayOfWeek: selectedDate.getDay(),
          customDuration,
        }],

        // Normalized kit FK persisted on the booking-show row (kit_id). Kept in
        // the details JSON below too for the label / backward-compat.
        kitId: kit ? Number(kit) : null,

        // Store the chosen event (show), times + kit block in details JSON.
        details: JSON.stringify({
          eventType,
          eventTypeLabel: selectedEvent?.label ?? null,
          roadShowStartTime: startTime,
          roadShowEndTime: endTime,
          roadShowDate: format(selectedDate, "yyyy-MM-dd"),
          kit: kit || null,
          kitLabel: kits.find((k) => k.value === kit)?.label ?? null,
        }),
        // Notes for display purposes
        notes: `Road Show Booking\nDate: ${format(selectedDate, "yyyy-MM-dd")}\nTime: ${startTime} - ${endTime}`,
      };

      // Create booking using the actual venue ID
      const response = await fetch(`/api/staff/venues/${venueId}/bookings/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingData),
      });

      const result = await response.json();
      console.log("API response:", result);

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to create road show booking");
      }

      const bookingId = result.data?.bookingId || result.bookingId || result.data?.id || result.id;

      if (!bookingId) {
        throw new Error("No booking ID returned from server");
      }

      toast.success("Road show booking created successfully");

      // Redirect to booking detail page
      router.push(`/${portalType}/venue/${venueId}/bookings/${bookingId}`);

      // Reset form but keep modal open
      setCustomerName("");
      setCustomerEmail("");
      setEventType("");
      setKit("");
      setParticipants("30");
      setSelectedDate(undefined);
      setStartTime("09:00");
      setEndTime("17:00");
    } catch (error) {
      console.error("Error creating road show booking:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create road show booking");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            New road show booking{venueName ? ` — ${venueName}` : ""}
          </DialogTitle>
          <DialogDescription>
            Enter the details for the road show booking
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4 flex-1 overflow-y-auto -mx-6 px-6">
          {/* Customer Name */}
          <div className="grid gap-2">
            <Label htmlFor="customerName">
              Customer name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="customerName"
              placeholder="Enter customer name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>

          {/* Customer Email */}
          <div className="grid gap-2">
            <Label htmlFor="customerEmail">
              Customer email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="customerEmail"
              type="email"
              placeholder="Enter customer email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
            />
          </div>

          {/* Event type */}
          <div className="grid gap-2">
            <Label>
              Event type <span className="text-red-500">*</span>
            </Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isLoadingOptions
                      ? "Loading events…"
                      : events.length === 0
                        ? "No road shows configured"
                        : "Select an event type"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {events.map((e) => (
                  <SelectItem key={e.value} value={e.value}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isLoadingOptions && events.length === 0 && (
              <p className="text-xs text-amber-600">
                Create a road show under Settings → Shows (Type: Road show)
                first.
              </p>
            )}
          </div>

          {/* Kit (optional / informational — doc §10.1) */}
          <div className="grid gap-2">
            <Label>Kit</Label>
            <Select value={kit} onValueChange={setKit}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isLoadingOptions
                      ? "Loading kits…"
                      : kits.length === 0
                        ? "No kits configured"
                        : "Select a kit"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {kits.map((k) => (
                  <SelectItem key={k.value} value={k.value}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Participants */}
          <div className="grid gap-2">
            <Label htmlFor="participants">
              Participants <span className="text-red-500">*</span>
            </Label>
            <Input
              id="participants"
              type="number"
              min={1}
              max={selectedEvent?.max ?? undefined}
              value={participants}
              onChange={(e) => setParticipants(e.target.value)}
            />
            {selectedEvent && (
              <p
                className={`text-xs ${belowMin ? "text-amber-600" : "text-muted-foreground"}`}
              >
                {selectedEvent.max != null
                  ? `${selectedEvent.min}–${selectedEvent.max} participants`
                  : `Min ${selectedEvent.min} participants (recommended)`}
                {belowMin ? " — below the recommended minimum" : ""}
              </p>
            )}
          </div>

          {/* Date Selection */}
          <div className="grid gap-2">
            <Label>
              Show date <span className="text-red-500">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Show time (customer-facing; shown on the confirmation & calendar) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="startTime">
                Show start <span className="text-red-500">*</span>
              </Label>
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="endTime">
                Show end <span className="text-red-500">*</span>
              </Label>
              <Input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isLoading}>
            {isLoading ? "Creating..." : "Create road show"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}